import { useCallback, useEffect, useMemo, useState } from 'react'

import { requestDailyPepTalk } from '../_data/renter-llm'
import {
  buildDailyPepTalkSnapshot,
  formatDateString,
} from '../_data/daily-pep-talk-snapshot'
import {
  CachedDailyPepTalk,
  DailyPepTalkSnapshot,
  UseDailyPepTalkValue,
} from '../_types/daily-pep-talk'
import { useAuth } from './use-auth'
import { useImportedListings } from './use-imported-listings'
import { useListingDecisions } from './use-listing-decisions'
import { useQuickPassProfile } from './use-quickpass-profile'

const CACHE_PREFIX = 'quickpass.dailyPepTalk:v1:'

const readCache = (
  userId: string,
  dateString: string,
): CachedDailyPepTalk | null => {
  try {
    const raw = localStorage.getItem(`${CACHE_PREFIX}${userId}`)
    if (raw === null) {
      return null
    }
    const parsed = JSON.parse(raw) as CachedDailyPepTalk
    if (parsed.dateString !== dateString) {
      return null
    }
    return parsed
  } catch (error) {
    console.error('[daily-pep-talk] cache read failed', error)
    return null
  }
}

const writeCache = (userId: string, payload: CachedDailyPepTalk): void => {
  try {
    localStorage.setItem(`${CACHE_PREFIX}${userId}`, JSON.stringify(payload))
  } catch (error) {
    console.error('[daily-pep-talk] cache write failed', error)
  }
}

/**
 * Once-per-day-per-user pep-talk for the dashboard. Computes a snapshot
 * from profile + listings + tags, calls the LLM at most once per
 * calendar day, and caches the result.
 *
 * @returns {UseDailyPepTalkValue} the pep-talk state + manual refresh
 */
const useDailyPepTalk = (): UseDailyPepTalkValue => {
  const { quickPassUser } = useAuth()
  const { profile } = useQuickPassProfile()
  const { listings } = useImportedListings()
  const { tags } = useListingDecisions()

  const [state, setState] = useState<UseDailyPepTalkValue['state']>('idle')
  const [result, setResult] = useState<UseDailyPepTalkValue['result']>(null)
  const [generatedAt, setGeneratedAt] = useState<number | null>(null)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [forceRefreshToken, setForceRefreshToken] = useState<number>(0)

  const snapshot = useMemo<DailyPepTalkSnapshot | null>(() => {
    if (profile === null) {
      return null
    }
    return buildDailyPepTalkSnapshot(profile, listings, tags, Date.now())
  }, [profile, listings, tags])

  const userId = quickPassUser?.id ?? ''

  useEffect(() => {
    if (userId === '' || snapshot === null) {
      return
    }

    const dateString = formatDateString(Date.now())
    const cached =
      forceRefreshToken === 0 ? readCache(userId, dateString) : null

    if (cached !== null) {
      setResult(cached.result)
      setGeneratedAt(cached.generatedAt)
      setState('ready')
      return
    }

    let isMounted = true
    setState('loading')
    setErrorMessage('')

    const run = async (): Promise<void> => {
      try {
        const llmResult = await requestDailyPepTalk({ snapshot })

        if (isMounted === false) {
          return
        }

        if (llmResult === null) {
          setState('offline')
          return
        }

        const fresh: CachedDailyPepTalk = {
          dateString,
          result: llmResult,
          generatedAt: Date.now(),
        }
        writeCache(userId, fresh)
        setResult(llmResult)
        setGeneratedAt(fresh.generatedAt)
        setState('ready')
      } catch (error) {
        if (isMounted === false) {
          return
        }
        setState('error')
        setErrorMessage(
          error instanceof Error
            ? error.message
            : "Could not pull today's pep-talk.",
        )
      }
    }

    void run()

    return () => {
      isMounted = false
    }
  }, [userId, snapshot, forceRefreshToken])

  const refresh = useCallback(async (): Promise<void> => {
    if (userId !== '') {
      try {
        localStorage.removeItem(`${CACHE_PREFIX}${userId}`)
      } catch (error) {
        console.error('[daily-pep-talk] cache clear failed', error)
      }
    }
    setForceRefreshToken(token => token + 1)
  }, [userId])

  return {
    state,
    result,
    generatedAt,
    errorMessage,
    refresh,
  }
}

export { useDailyPepTalk }
